import { ToastrService } from 'ngx-toastr';
import QRCode from 'qrcode';
import { firstValueFrom } from 'rxjs';

import type { AfterViewInit, ElementRef } from '@angular/core';
import {
	 ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, QueryList, ViewChildren
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';

import { FormBaseComponent } from '@bp/shared/components/core';
import { Validators } from '@bp/shared/features/validation';
import { ensureFormGroupConfig } from '@bp/shared/typings';
import { FADE_IN } from '@bp/shared/animations';
import { firstPresentQueryListItem } from '@bp/shared/utilities';
import { OnChanges, SimpleChanges } from '@bp/shared/models/core';
import { GtagEvents } from '@bp/shared/features/analytics';
import { AsyncVoidSubject } from '@bp/shared/rxjs';

import type { IRegisterAuthenticatorAppApiRequest } from '@bp/admins-shared/domains/identity';

@Component({
	selector: 'bp-register-authenticator-app-form',
	templateUrl: './register-authenticator-app-form.component.html',
	styleUrls: [ './register-authenticator-app-form.component.scss' ],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [ FADE_IN ],
})
export class RegisterAuthenticatorAppFormComponent
	extends FormBaseComponent<IRegisterAuthenticatorAppApiRequest>
	implements OnChanges, AfterViewInit {

	private _authenticatorKey!: string | null;

	@Input() userEmail?: string | null;

	@Input() submitButtonText!: string;

	@Input() submitButtonGtagEventName!: GtagEvents.List;

	@Input()
	get authenticatorKey(): string | null {
		return this._authenticatorKey;
	}

	set authenticatorKey(value: string | null) {
		this._authenticatorKey = value;

		this.controls!.key!.setValue(value);
	}

	@Output() readonly login = new EventEmitter();

	@ViewChildren('qrCodeCanvas')
	qrCodeCanvasQueryList!: QueryList<ElementRef>;

	override onSubmitShowInvalidInputsToast = false;

	private readonly _viewInited$ = new AsyncVoidSubject();

	constructor(
		fb: UntypedFormBuilder,
		cdr: ChangeDetectorRef,
		toaster: ToastrService,
	) {
		super(fb, cdr, toaster);

		this.form = this._formBuilder.group(ensureFormGroupConfig<IRegisterAuthenticatorAppApiRequest>({
			key: [ null, []],
			code: [ null, [ Validators.required ]],
		}));
	}

	override ngOnChanges(changes: SimpleChanges<this>): void {
		super.ngOnChanges(changes);

		if ((changes.userEmail || changes.authenticatorKey) && this.userEmail && this.authenticatorKey)
			void this._mountAuthenticatorQRCode();
	}

	ngAfterViewInit(): void {
		this._viewInited$.complete();
	}

	private async _mountAuthenticatorQRCode(): Promise<void> {
		await firstValueFrom(this._viewInited$);

		const $QRCodeCanvas = await firstPresentQueryListItem(
			this.qrCodeCanvasQueryList,
			elementRef => <HTMLCanvasElement>elementRef.nativeElement,
		);

		const issuer = 'bridgerpay';

		void QRCode.toCanvas(
			$QRCodeCanvas,
			`otpauth://totp/${ issuer }:${ this.userEmail }?secret=${ this.authenticatorKey }&issuer=${ issuer }`,
			{
				margin: 0,
				width: 175,
			},
		);
	}

}
